import React from 'react';

type SetStateAction<S> = S | ((prevState: S) => S);

//eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useLocalStorage<T = any>(
  key: string,
  initialValue: T,
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [state, setState] = React.useState<T>(initialValue);
  // Captured once on first render and used as the empty-storage fallback. Kept
  // in a ref so the read effect can depend only on `key`: callers commonly pass
  // a fresh `initialValue` literal each render, which would otherwise re-read
  // storage on every render.
  const initialValueRef = React.useRef(initialValue);

  React.useEffect(() => {
    try {
      const value = window.localStorage.getItem(key);
      // Re-read when `key` changes — otherwise the hook keeps serving the value
      // it read for the previous key.
      setState(value ? (JSON.parse(value) as T) : initialValueRef.current);
    } catch {
      setState(initialValueRef.current);
    }
  }, [key]);

  const setValue: React.Dispatch<React.SetStateAction<T>> = React.useCallback(
    (value: SetStateAction<T>) => {
      try {
        // If the passed value is a callback function,
        // then call it with the existing state.
        setState((prevState) => {
          const valueToStore = value instanceof Function ? value(prevState) : value;
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
          return valueToStore;
        });
      } catch {
        // swallow — localStorage may be unavailable (quota, private mode, etc.)
      }
    },
    [key],
  );

  return [state, setValue];
}
